import { router } from "@inertiajs/react";
import { Globe } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { organizationSettingsOrganizationPath } from "@/routes";

interface PublicCatalogBannerProps {
  enabled: boolean;
  organizationSlug: string;
}

// Hardcoded by request to keep the contact flow simple in v0.2.
const CONTACT_EMAIL = "soporte@tech-sed.com";

export function PublicCatalogBanner({
  enabled,
  organizationSlug,
}: PublicCatalogBannerProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (enabled) {
    return null;
  }

  const handleEnable = () => {
    setIsSubmitting(true);
    router.patch(
      organizationSettingsOrganizationPath(organizationSlug),
      { organization: { public_catalog_enabled: true } },
      {
        onFinish: () => {
          setIsSubmitting(false);
          setIsDialogOpen(false);
        },
      }
    );
  };

  return (
    <>
      <Item
        variant="outline"
        size="sm"
        className="border-blue-200 bg-blue-50 hover:bg-blue-50"
      >
        <ItemMedia variant="icon" className="border-blue-200 bg-white">
          <Globe className="size-4 text-blue-600" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle className="text-blue-950">
            Hay un catálogo público disponible
          </ItemTitle>
          <ItemDescription className="text-xs text-blue-800">
            Activa para ver productos, categorías y proveedores ya listos.
            Si queres cambiar algo, lo podes personalizar solo para tu
            organización. Si quieres ver una muestra,{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Quiero%20ver%20una%20muestra%20del%20catálogo%20público`}
              className="font-medium text-blue-600 hover:underline"
            >
              envía un correo
            </a>
            .
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button
            variant="default"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
          >
            Habilitar
          </Button>
        </ItemActions>
      </Item>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Habilitar catalogo publico</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Vas a ver productos, categorias y proveedores compartidos por
                otras organizaciones.
              </p>
              <p>
                Si queres renombrar o ajustar algo, se guarda solo en tu
                organizacion. Lo de los demas no se modifica.
              </p>
              <p className="font-medium text-amber-600">
                Esta accion no se puede deshacer.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleEnable} disabled={isSubmitting}>
              {isSubmitting ? "Habilitando..." : "Habilitar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
